import { SUPPORTED_LOCALES, type SupportedLocale } from "./i18n";

/**
 * Maps a BCP-47 tag onto a supported locale by its PRIMARY subtag, stripping any region/script
 * subtag (`en-GB` -> `en`, `fr-CA` -> `fr`); returns `undefined` for anything the platform does not
 * offer (`de-DE` -> `undefined`).
 */
export function toSupportedLocale(tag: string | null | undefined): SupportedLocale | undefined {
  if (!tag) return undefined;
  const primary = tag.split("-")[0]?.toLowerCase();
  return SUPPORTED_LOCALES.find((locale) => locale === primary);
}

/**
 * Diner interface-language resolution (localization-i18n R1) — the client-side TWIN of the backend
 * `DinerLanguageResolution.resolve`, locked to it by the shared `i18n-resolution-cases.json` fixture
 * both suites iterate (so drift is a test failure on both sides).
 *
 * Precedence, highest first:
 *  1. `explicit` — the diner's in-session choice (the `lang` query param / stored session pick);
 *  2. `browserLanguages` — the first SUPPORTED tag (region stripped, unsupported tags skipped);
 *  3. `venueDefault` — always present.
 *
 * An unsupported explicit/browser tag is ignored, never fatal; the chain always terminates on a
 * supported locale (defensively degrading to `"fr"` should the venue default fall outside the set).
 */
export function resolveDinerLanguage(
  explicit: string | null | undefined,
  browserLanguages: readonly string[],
  venueDefault: string,
): SupportedLocale {
  const fromExplicit = toSupportedLocale(explicit);
  if (fromExplicit) return fromExplicit;
  for (const tag of browserLanguages) {
    const supported = toSupportedLocale(tag);
    if (supported) return supported;
  }
  return toSupportedLocale(venueDefault) ?? "fr";
}
